import React, { useCallback, useMemo } from 'react'
import moment from 'moment'
import CharterDurationToggleControl, { CharterDurationToggleOption } from 'components/OfferPage/Filters/CharterFlight/CharterDurationToggleControl'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPageActions, OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import { OfferDurationOption } from 'components/OfferPage/Filters/DurationTabs/OfferDurationTabs'
import { CHARTER_BUNDLE_DURATION_OPTIONS } from 'constants/charterFlight'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { FlightsClassTypes } from 'constants/flight'
import { useFlightItemData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useFlightItemData'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { useIntl } from 'react-intl'

interface Props {
  charterBundleRoute?: App.CharterBundleRoute
  duration: number
  durationOptions: Array<OfferDurationOption>
  checkInDate?: string
  rooms: Array<OfferPageRoom>
  priceMode: OfferPagePriceMode
  isMemberPrice: boolean
  fareClass?: FlightsClassTypes
}

function CharterDurationToggle(props: Props) {
  const {
    charterBundleRoute,
    duration,
    durationOptions,
    checkInDate,
    rooms,
    priceMode,
    isMemberPrice,
    fareClass = FlightsClassTypes.ECONOMY,
  } = props

  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)

  const [primaryDuration, secondaryDuration] = CHARTER_BUNDLE_DURATION_OPTIONS.map(option => option.value)
  const hasRequiredData = charterBundleRoute && primaryDuration && secondaryDuration

  // Check if the hotel has packages for both durations
  // If the hotel only offers one duration (e.g., only 5 nights, not 12), hide the toggle
  const hasPrimaryDurationPackage = durationOptions.some(opt => opt.duration === primaryDuration && !opt.soldOut)
  const hasSecondaryDurationPackage = durationOptions.some(opt => opt.duration === secondaryDuration && !opt.soldOut)
  const hasBothDurationOptions = hasPrimaryDurationPackage && hasSecondaryDurationPackage

  // Calculate checkOut dates for the two duration options based on current checkIn
  const dateRangesForDurations = useMemo(() => {
    if (!checkInDate) {
      return []
    }

    return [primaryDuration, secondaryDuration]
      .filter(Boolean)
      .map((durationNights: number) => {
        const checkOut = moment(checkInDate).add(durationNights, 'days').format(ISO_DATE_FORMAT)
        return {
          checkIn: checkInDate,
          checkOut,
          durationNights,
        }
      })
  }, [checkInDate, primaryDuration, secondaryDuration])

  // Fetch flight data only for the two duration options
  const primaryFlightData = useFlightItemData({
    checkIn: dateRangesForDurations[0]?.checkIn ?? '',
    checkOut: dateRangesForDurations[0]?.checkOut ?? '',
    rooms: rooms || [{ adults: 2, children: 0, infants: 0, teenagers: 0, childrenAge: [] }],
    fareClass,
    charterBundleRoute,
  })

  const secondaryFlightData = useFlightItemData({
    checkIn: dateRangesForDurations[1]?.checkIn ?? '',
    checkOut: dateRangesForDurations[1]?.checkOut ?? '',
    rooms: rooms || [{ adults: 2, children: 0, infants: 0, teenagers: 0, childrenAge: [] }],
    fareClass,
    charterBundleRoute,
  })

  // Check if flight data is still loading
  const isFlightDataLoading = primaryFlightData.fetching || secondaryFlightData.fetching

  // Map flight data to duration prices
  const flightPricesByDuration = useMemo(() => {
    const prices: Record<number, number | undefined> = {}

    if (dateRangesForDurations[0] && primaryFlightData.totalFlightFare) {
      prices[dateRangesForDurations[0].durationNights] = primaryFlightData.totalFlightFare
    }

    if (dateRangesForDurations[1] && secondaryFlightData.totalFlightFare) {
      prices[dateRangesForDurations[1].durationNights] = secondaryFlightData.totalFlightFare
    }

    return prices
  }, [dateRangesForDurations, primaryFlightData.totalFlightFare, secondaryFlightData.totalFlightFare])

  const convertToDisplay = useMemo(() => getPriceDisplayFunction(priceMode, rooms), [priceMode, rooms])

  const shouldPreferMemberPrice = useCallback((option?: OfferDurationOption): boolean => {
    if (!luxPlusEnabled) return false

    const nonMemberPrice = option?.lowestPrice
    const memberPrice = option?.lowestMemberPrice

    const hasValidPrices = typeof nonMemberPrice === 'number' && typeof memberPrice === 'number'
    if (!hasValidPrices) return false

    const isMemberPriceCheaper = memberPrice > 0 && memberPrice < nonMemberPrice
    return isMemberPriceCheaper
  }, [luxPlusEnabled])

  const getDurationPrice = useCallback((nights: number) => {
    const option = durationOptions.find(durationOption => durationOption.duration === nights)
    // Match `LuxPlusPriceStack` behavior: even for non-members we can show the LuxPlus price prominently
    // when LuxPlus is enabled and the member price is cheaper than the non-member price.
    const hotelPrice = shouldPreferMemberPrice(option)
      ? option?.lowestMemberPrice
      : option?.lowestPrice
    if (!hotelPrice) {
      return undefined
    }
    // For charter bundle, add flight price to hotel price
    // Only return a price when we have BOTH hotel and flight prices to avoid showing incomplete prices
    if (charterBundleRoute) {
      const flightPrice = flightPricesByDuration[nights]
      if (flightPrice) {
        const totalPrice = hotelPrice + flightPrice
        return convertToDisplay(totalPrice)
      }
      // If we have a checkIn date but no flight price yet, return undefined to show loading
      // If no checkIn date, we can't calculate flight price so return undefined
      return undefined
    }

    return convertToDisplay(hotelPrice)
  }, [durationOptions, convertToDisplay, charterBundleRoute, flightPricesByDuration, shouldPreferMemberPrice])

  const onToggleChange = useCallback((parsedNights: number) => {
    if (!Number.isFinite(parsedNights) || parsedNights === duration) {
      return
    }

    if (checkInDate) {
      const newCheckOut = moment(checkInDate).add(parsedNights, 'days').format(ISO_DATE_FORMAT)
      dispatch({
        type: OfferPageActions.setDates,
        checkIn: checkInDate,
        checkOut: newCheckOut,
      })
      return
    }

    dispatch({ type: OfferPageActions.setDuration, duration: parsedNights })
  }, [checkInDate, dispatch, duration])

  const selectedValue = useMemo(() => {
    if (duration === primaryDuration || duration === secondaryDuration) {
      return duration
    }
    return primaryDuration ?? duration
  }, [duration, primaryDuration, secondaryDuration])

  const primaryPrice = getDurationPrice(primaryDuration)

  const secondaryPrice = getDurationPrice(secondaryDuration)

  const durationLabelLookup = useMemo(() => new Map(CHARTER_BUNDLE_DURATION_OPTIONS.map(option => [option.value, option.label])), [])

  const durationToggleOptions = useMemo<ReadonlyArray<CharterDurationToggleOption>>(() => {
    if (!primaryDuration || !secondaryDuration) {
      return []
    }
    // Only disable secondary option if we have a checkInDate but flight prices failed to load
    // Don't disable when no dates are selected - user should be able to toggle between durations
    const shouldDisableSecondary = !!checkInDate && !isFlightDataLoading && secondaryPrice === undefined
    return [{
      value: primaryDuration,
      label: durationLabelLookup.get(primaryDuration) ?? intl.formatMessage(
        {
          id: 'w5r0aVbs4v',
          defaultMessage: '{nights} nights & flights',
          description: 'Label for charter duration toggle showing nights and flights',
        },
        {
          nights: primaryDuration,
        },
      ),
      displayPrice: primaryPrice,
    }, {
      value: secondaryDuration,
      label: durationLabelLookup.get(secondaryDuration) ?? intl.formatMessage(
        {
          id: 'w5r0aVbs4v',
          defaultMessage: '{nights} nights & flights',
          description: 'Label for charter duration toggle showing nights and flights',
        },
        {
          nights: secondaryDuration,
        },
      ),
      displayPrice: secondaryPrice,
      disabled: shouldDisableSecondary,
    }]
  }, [durationLabelLookup, primaryDuration, secondaryDuration, primaryPrice, secondaryPrice, checkInDate, isFlightDataLoading, intl])

  // Hide the toggle if:
  // - Missing required data (charterBundleRoute, durations)
  // - Hotel doesn't have packages for both duration options
  // - Toggle options couldn't be built
  if (!hasRequiredData || !hasBothDurationOptions || durationToggleOptions.length < 2) {
    return null
  }

  // Show loading state when we have dates selected but flight prices aren't ready yet
  const shouldShowPricesLoading = !!checkInDate && isFlightDataLoading

  return (
    <CharterDurationToggleControl
      options={durationToggleOptions}
      selectedValue={selectedValue}
      unavailableMessage={intl.formatMessage({
        id: 'AV3K6R57jR',
        defaultMessage: 'Unavailable for your dates',
        description: 'Message shown when charter duration option is unavailable for selected dates',
      })}
      onChange={onToggleChange}
      isMemberPrice={canViewLuxPlusBenefits && isMemberPrice}
      pricesLoading={shouldShowPricesLoading}
    />
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  charterBundleRoute: state.charterBundleRoute,
  duration: state.duration,
  checkInDate: state.checkInDate,
  durationOptions: getDurationOptions(state, appState),
  rooms: state.rooms,
  priceMode: state.priceMode,
  isMemberPrice: state.displayMemberPrice,
  fareClass: state.fareClass,
}), CharterDurationToggle)
